import { ViewConstructor, View } from "../view";
import { BaseApp } from "../app";
import ViewChain from "./view-chain";
import { getObjectProperty } from "../helpers/index";

/**
 * Represents a router route.
 */
export class Route
{
    private app: BaseApp;
    public pattern: RegExp;
    public viewType: ViewConstructor | ViewConstructor[];
    private path: string;

    public options: RouteOptions;

    /** Whether or not the route is a chain (multiple views). */
    public get isChain() { return Array.isArray(this.viewType); }

    /**
     * Creates a new Route.
     * @param app App
     * @param pattern Pattern used to trigger the route. 
     * @param viewType Target view.
     * @param initial Initial data for views.
     */
    constructor(app: BaseApp, pattern: RoutePattern, viewType: ViewConstructor | ViewConstructor[], options?: RouteOptions)
    {
        this.app = app;
        if (!(pattern instanceof RegExp))
        {
            pattern = new RegExp(`^${pattern}$`, 'g');
        }
        else if(pattern.flags.indexOf('g') === -1)
        {
            pattern = new RegExp(pattern.source, 'g');
        }
         
        this.pattern = pattern;
        this.viewType = viewType;
        this.options = options;
    }

    /**
     * Checks if the route matches a path.
     * @param path The path to check.
     */
    public match(path: string)
    {
        this.path = path;

        // reset regex
        this.pattern.lastIndex = 0;
        let result = this.pattern.test(this.path);
        return result;
    }

    private buildRequest()
    {
        this.app.request = {
            path: location.hash,
            parameters: [],
            state: history.state
        }

        let match;
        let protection = 0;

        if (this.path.length > 0)
        {
            this.pattern.lastIndex = 0;

            let matches: RegExpExecArray;

            if ((matches = this.pattern.exec(this.path)) !== null) 
            {
                if (matches.length > 1)
                {
                    this.app.request.parameters =  matches.splice(1).map(param => decodeURIComponent(param));
                }
            }
        }
    }

    private prepareRun(): PromiseLike<void>
    {
        if (!this.app.isAuthenticated && getObjectProperty(this.options, 'requireAuthentication', true))
        {
            return this.app.authenticate();
        }
        else
        {
            return Promise.resolve();
        }
    }
    /**
     * Executes the route.
     */
    public run(data?: any): PromiseLike<void>
    {
        this.buildRequest();

        return this.prepareRun().then(() =>
        {
            if (Array.isArray(this.viewType))
            {
                let viewChain = new ViewChain(this.app, this);
                return viewChain.run();
            }
            else
            {
                return this.app.view(this.viewType, data ? data : getObjectProperty(this.options, 'initial')).then(() =>
                {
                    
                });
            }
        });
    }
}

/** Route pattern type. */
export type RoutePattern = string | RegExp;

export interface RouteOptions
{
    initial?: any;
    requireAuthentication?: boolean;
}